import { DropEvent, ErrorCode, FileError, FileRejection, useDropzone } from "react-dropzone";
import { MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB, MAX_FILE_TO_UPLOAD } from "../utils/constants";
import React from "react";
import { errorToast } from "../utils/toastNotification";
import {
  getMaxSizeDisplayText,
  isInValidAspectRatio,
  isInValidDimension,
  isInvalidMaxWidth,
  isInvalidMinWidthAndMaxHeight
} from "./core/assetUpload/util";
import { getImageDimensionInfo, getKBInBytes, getKBInMB, isFileTypeImage } from "../utils";
import { useCopies } from "shared/hooks/useCopies";
import ImageCompressor from "../utils/imageCompressor";
import { useCommonFeatures } from "../../context/commonFeatures";
import { AspectRatioRestrictionType, AssetConfig, DimensionType } from "./core/assetUpload/types";
import { AssetUploadErrorCodeType } from "./types";
import { CustomAssetUploadErrorCode } from "./enum";

export default function useDropZoneFileService({
  onFileSelectError,
  maxSizeAllowed = MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB,
  onFileSelect,
  compressFile = true,
  ...props
}: {
  acceptedMimeType: string;
  disabled?: boolean;
  maxSizeAllowed?: number; // in KB
  compressFile?: boolean;
  onFileSelect: (f: Array<File>) => void;
  onFileSelectError?: (errorCode: AssetUploadErrorCodeType) => void;
  assetConfig?: AssetConfig;
  aspectRatioRestriction?: AspectRatioRestrictionType;
}) {
  const copies = useCopies();
  const { showLoadingIndicator } = useCommonFeatures();

  const errorHandler = React.useCallback(
    (errorCode: AssetUploadErrorCodeType) => {
      if (onFileSelectError) {
        onFileSelectError?.(errorCode);
      } else {
        switch (errorCode) {
          case ErrorCode.FileInvalidType:
            errorToast(
              copies.get("invalid.file.type.error", {
                0: props.acceptedMimeType
              })
            );
            break;
          case ErrorCode.FileTooLarge:
            errorToast(
              copies.get("max.file.size.error", {
                0: getMaxSizeDisplayText(maxSizeAllowed)
              })
            );
            break;
          case ErrorCode.FileTooSmall:
            errorToast(copies.get("min.file.size.error"));
            break;
          case ErrorCode.TooManyFiles:
            errorToast(copies.get("too.many.file.error", { 0: MAX_FILE_TO_UPLOAD }));
            break;
          case CustomAssetUploadErrorCode.ASPECT_RATIO_INCORRECT:
            errorToast(copies.get("incorrect.aspect.ratio.error.msg"));
            break;
          case CustomAssetUploadErrorCode.DIMENSION_INCORRECT:
            errorToast(copies.get("incorrect.image.dimension.error.msg"));
            break;
          default:
            errorToast(copies.get("unknown.error.in.asset.upload"));
        }
      }
    },
    [onFileSelectError, props.acceptedMimeType, copies, maxSizeAllowed]
  );

  const getCustomFileFromEvent = React.useCallback(
    async (event: DropEvent | Array<FileSystemFileHandle>) => {
      try {
        const files = await getCustomCompressedFileGetter(event, maxSizeAllowed, p => {
          showLoadingIndicator(p < 100);
        });
        return getImagesWithDimensionInfo(files);
      } catch (e) {
        console.error(e);
        showLoadingIndicator(false);
        return null as any;
      }
    },
    [maxSizeAllowed, showLoadingIndicator]
  );

  const dropzoneState = useDropzone({
    maxFiles: MAX_FILE_TO_UPLOAD,
    noClick: true,
    noKeyboard: true,
    accept: props.acceptedMimeType,
    disabled: props.disabled,
    maxSize: getKBInBytes(maxSizeAllowed),
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles);
      }
      if (fileRejections.length > 0) {
        const errorCode = fileRejections[0]?.errors[0]?.code;
        errorHandler(errorCode as AssetUploadErrorCodeType);
      }
    },
    getFilesFromEvent: compressFile ? getCustomFileFromEvent : undefined,
    validator: (file: File) => {
      if (isFileTypeImage(file) && props.assetConfig) {
        if (
          props.aspectRatioRestriction === AspectRatioRestrictionType.ERROR &&
          isInValidAspectRatio({
            //@ts-ignore Intentional.
            fileDimension: { width: file.width, height: file.height },
            recommendedDimension: props.assetConfig.dimension
          })
        ) {
          return {
            code: CustomAssetUploadErrorCode.ASPECT_RATIO_INCORRECT
          } as FileError;
        }
        if (
          props.aspectRatioRestriction === AspectRatioRestrictionType.STRICT_DIMENSION &&
          isInValidDimension({
            //@ts-ignore Intentional.
            fileDimension: { width: file.width, height: file.height },
            recommendedDimension: props.assetConfig.dimension
          })
        ) {
          return {
            code: CustomAssetUploadErrorCode.DIMENSION_INCORRECT
          } as FileError;
        }
        if (
          props.aspectRatioRestriction === AspectRatioRestrictionType.STRICT_MIN_WIDTH_MAX_HEIGHT &&
          isInvalidMinWidthAndMaxHeight({
            //@ts-ignore Intentional.
            fileDimension: { width: file.width, height: file.height },
            recommendedDimension: props.assetConfig.dimension
          })
        ) {
          return {
            code: CustomAssetUploadErrorCode.DIMENSION_INCORRECT
          } as FileError;
        }
        if (
          props.aspectRatioRestriction === AspectRatioRestrictionType.STRICT_MAX_WIDTH &&
          isInvalidMaxWidth({
            //@ts-ignore Intentional.
            fileDimension: { width: file.width },
            recommendedDimension: props.assetConfig.dimension
          })
        ) {
          return {
            code: CustomAssetUploadErrorCode.DIMENSION_INCORRECT
          } as FileError;
        }
      }
      return null;
    }
  });

  return { dropzoneState };
}

/*
 * Below Function is used to get Custom file Object and compress image. Below 2 cases were handled
 * 1. If User Drag/Drop then event will be of DropEvent
 * 2. If User open file explorer and select then event will be of Array<FileSystemFileHandle>
 * */
async function getCustomCompressedFileGetter(
  event: DropEvent | Array<FileSystemFileHandle>,
  maxSizeInKB: number,
  compressProgressCB: (progress: number) => void
) {
  if (Array.isArray(event)) {
    return Promise.all(
      event.map(async (i: FileSystemFileHandle) => {
        const file = await i.getFile();
        return ImageCompressor.getCompressedImage(file, {
          maxSizeMB: getKBInMB(maxSizeInKB),
          onProgress: compressProgressCB
        });
      })
    );
  } else {
    const files = [];
    const e = event as any;
    const fileList = e?.dataTransfer ? e.dataTransfer?.files : e?.target?.files;
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      files.push(file);
    }
    return Promise.all(
      files.map(async (file: File) => {
        return ImageCompressor.getCompressedImage(file, {
          maxSizeMB: getKBInMB(maxSizeInKB),
          onProgress: compressProgressCB
        });
      })
    );
  }
}

export function getImagesWithDimensionInfo(files: Array<File>) {
  const promises = [];
  try {
    for (let index = 0; index < files.length; index++) {
      const file: any = files[index]; // Setting type as any so that can assign width and height properties
      const promise = new Promise((resolve, reject) => {
        if (isFileTypeImage(file)) {
          getImageDimensionInfo(URL.createObjectURL(file)).then((dimension: DimensionType) => {
            file.width = dimension.width;
            file.height = dimension.height;
            resolve(file);
          });
        } else {
          resolve(file);
        }
      });
      promises.push(promise);
    }
    return Promise.all(promises);
  } catch (err) {
    console.log(err);
  }
}
